/**
 * Deferred spawning of boot-restored session tabs.
 *
 * On desktop App.tsx mounts EVERY session tab at once and toggles visibility
 * with `display` — a deliberate choice, because unmounting an xterm loses its
 * buffer and remounting it re-runs the whole spawn/resume flow. The cost is that
 * mounting a terminal spawns its CLI immediately, and each CLI process starts
 * its OWN copy of every MCP server configured for it. Restoring N session tabs
 * at launch therefore fires N x M server processes within a couple of seconds
 * (23 Claude tabs x 12 global servers ~= 275 `npx` spawns on a real machine),
 * which pushes past the CLIs' MCP startup timeouts — so the app boots with its
 * MCPs already marked failed.
 *
 * The fix is to defer, not to unmount: a tab restored from disk stays out of the
 * tree until the user first opens it, then mounts and stays mounted for the rest
 * of the run exactly like every other tab.
 *
 * The deferred set holds ONLY ids restored at boot. Tabs created at runtime are
 * never in it, so they mount the instant they're added — this matters for the
 * Project Manager, which spawns background sessions WITHOUT focusing them and
 * needs them running unattended.
 */

/**
 * Should a session tab be mounted (and therefore allowed to spawn its CLI)?
 *
 * `isActive` wins: the tab the user is looking at always mounts, including the
 * one restored as active at boot.
 *
 * Mobile used to mount *only* the active tab. That unmounted an agent's xterm
 * every time the user switched away, discarding its buffer — coming back gave
 * a blank terminal, because a TUI only writes when something changes. Replaying
 * saved scrollback is not an option for those tabs the way it is for a plain
 * shell: a cursor-addressed redraw replayed into a fresh grid renders as
 * garbage. So the relay client now follows the same rule as the desktop, and the
 * deferred gate below is what keeps the cost bounded — a phone restoring
 * twenty tabs at boot still only mounts the ones the user actually opens.
 */
export function shouldMountSessionTab(opts: {
  isActive: boolean
  /**
   * Kept in the signature: callers pass it, and it documents the platform.
   * Deliberately unread — see the note above about mobile following the same
   * rule as the desktop. Renamed from `isMobile` when layout moved to media
   * queries: this was always about being the relay client, never the width.
   */
  isRemote: boolean
  /** Restored at boot and not yet opened in this run. */
  isDeferred: boolean
}): boolean {
  if (opts.isActive) return true
  return !opts.isDeferred
}

/**
 * Drop every just-opened tab from the deferred set.
 *
 * Returns the SAME set instance when nothing changed, so callers can use it
 * directly as a React state updater without causing a re-render on every tab
 * switch (and without looping when the effect re-runs on a fresh `splitTabIds`).
 */
export function undeferOpenedTabs(prev: Set<string>, opened: readonly string[]): Set<string> {
  if (!opened.some((id) => prev.has(id))) return prev
  const next = new Set(prev)
  for (const id of opened) next.delete(id)
  return next
}

/**
 * Every tab id that is being mounted right now, and so must leave the deferred
 * set — the invariant being that a deferred id is one nothing has mounted yet.
 *
 * Both mounting paths have to be covered or the set goes stale in the dangerous
 * direction. A split member left marked deferred while SplitViewContainer has it
 * live would be unmounted the moment the split is dissolved and the tab falls
 * back to the per-tab blocks — discarding the xterm buffer of a running session,
 * which is the exact loss the always-mounted design exists to prevent.
 */
export function collectMountedTabIds(opts: {
  activeTabId: string | null
  splitTabIds: ReadonlySet<string> | null
  /** Whether SplitViewContainer is in the tree — i.e. `splitView && !isSplitFullyDeferred(...)`. */
  splitMounted: boolean
}): string[] {
  const ids: string[] = []
  if (opts.activeTabId) ids.push(opts.activeTabId)
  if (opts.splitMounted && opts.splitTabIds) ids.push(...opts.splitTabIds)
  return ids
}

/**
 * True when a split view consists entirely of still-deferred tabs.
 *
 * Split panels are rendered by SplitViewContainer, not by the per-tab blocks in
 * App.tsx (those filter split members out), so `shouldMountSessionTab` never
 * sees them. Without this check a split restored at boot would spawn both of its
 * CLIs at launch through a path the per-tab gate can't reach.
 *
 * All-or-nothing on purpose: showing a split mounts every panel at once, so as
 * soon as one member has been opened the whole split is live.
 */
export function isSplitFullyDeferred(
  splitTabIds: ReadonlySet<string> | null,
  deferred: ReadonlySet<string>
): boolean {
  if (!splitTabIds || splitTabIds.size === 0) return false
  for (const id of splitTabIds) if (!deferred.has(id)) return false
  return true
}
